import type { ContentSegment, ParsedTool } from '@/components/aios-command/chat/message-parser';

export type SubagentLifecycleStatus =
    | 'running'
    | 'completed'
    | 'failed'
    | 'interrupted'
    | 'drained'
    /** Reloaded from persisted content_blocks — lifecycle events are not stored. */
    | 'restored';

export interface SubagentRun {
    subagentCallId: string;
    name: string;
    status: SubagentLifecycleStatus;
    error?: string | null;
    nestedSegments: ContentSegment[];
}

export interface SubagentTaggedFields {
    subagent: string | null;
    subagentCallId: string | null;
}

export interface StreamBlock {
    index: number;
    type: 'text' | 'thinking' | 'tool_use' | 'tool_result' | 'observation' | 'selection_request';
    content: string;
    toolId?: string;
    toolName?: string;
    toolInput?: unknown;
    toolOutput?: string;
    selection?: import('@/types/command-selection').SelectionRequest;
}

function normalizeToolCallId(id: unknown): string {
    return id != null ? String(id).trim() : '';
}

export function readSubagentTaggedFields(source: Record<string, unknown>): SubagentTaggedFields {
    const subagent =
        typeof source.subagent === 'string' && source.subagent.trim()
            ? source.subagent.trim()
            : null;
    const subagentCallId =
        source.subagent_call_id != null && String(source.subagent_call_id).trim()
            ? String(source.subagent_call_id).trim()
            : null;
    return { subagent, subagentCallId };
}

export function readSubagentCallIdFromEvent(event: Record<string, unknown>): string | null {
    return readSubagentTaggedFields(event).subagentCallId;
}

/**
 * Route stream frames to a subagent panel only when the frame is tagged as
 * delegated (`subagent` set). Missing call id → coordinator (flat fallback).
 */
export function resolveSubagentPanelId(source: Record<string, unknown>): string | null {
    const { subagent, subagentCallId } = readSubagentTaggedFields(source);
    if (!subagent || !subagentCallId) return null;
    return subagentCallId;
}

export function readDelegationPanelNameFromToolInput(input: unknown): string | null {
    if (!input || typeof input !== 'object') return null;
    const record = input as Record<string, unknown>;
    const raw = record.subagent_type ?? record.subagentType ?? record.subagent;
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return null;
}

/** Coordinator task frame that spawns a subagent panel (may arrive before subagent_start). */
export function isCoordinatorDelegationTaskTool(name: string, input: unknown): boolean {
    if (/^task$/i.test(name.trim())) return true;
    return readDelegationPanelNameFromToolInput(input) != null;
}

function inferSubagentNameFromSegments(segments: ContentSegment[]): string {
    for (const segment of segments) {
        if (segment.type === 'tool' && segment.tool?.subagent) {
            return segment.tool.subagent;
        }
    }
    return 'subagent';
}

function buildToolOutputsMap(sortedBlocks: StreamBlock[]): Map<number, string> {
    const toolOutputs = new Map<number, string>();
    for (let i = 0; i < sortedBlocks.length; i++) {
        const block = sortedBlocks[i];
        if (block.type === 'observation' && block.content) {
            for (let j = i - 1; j >= 0; j--) {
                if (sortedBlocks[j].type === 'tool_use') {
                    toolOutputs.set(sortedBlocks[j].index, block.content);
                    break;
                }
            }
        }
        if (block.type === 'tool_result' && block.content) {
            for (let j = i - 1; j >= 0; j--) {
                if (sortedBlocks[j].type === 'tool_use') {
                    toolOutputs.set(sortedBlocks[j].index, block.content);
                    break;
                }
            }
        }
        if (block.type === 'tool_use' && block.toolOutput != null && block.toolOutput !== '') {
            toolOutputs.set(block.index, block.toolOutput);
        }
    }
    return toolOutputs;
}

export function buildSegmentsFromBlockMap(
    blocks: Map<number, StreamBlock>,
    subagentTag?: SubagentTaggedFields,
): ContentSegment[] {
    const segments: ContentSegment[] = [];
    const sortedBlocks = Array.from(blocks.values()).sort((a, b) => a.index - b.index);
    const toolOutputs = buildToolOutputsMap(sortedBlocks);

    for (const block of sortedBlocks) {
        if (block.type === 'text') {
            if (block.content) {
                segments.push({ type: 'text', content: block.content });
            }
        } else if (block.type === 'tool_use') {
            const formattedName = (block.toolName || 'unknown').toLowerCase().replace(/\s+/g, '_');
            const output = toolOutputs.get(block.index);
            const tool: ParsedTool = {
                toolCallId: block.toolId || `tool-${block.index}`,
                type: `tool-${formattedName}`,
                state: output ? 'output-available' : 'input-available',
                input: block.toolInput,
                output,
                position: segments.filter((s) => s.type === 'tool').length,
                ...(subagentTag?.subagent != null ? { subagent: subagentTag.subagent } : {}),
                ...(subagentTag?.subagentCallId != null
                    ? { subagentCallId: subagentTag.subagentCallId }
                    : {}),
            };
            segments.push({ type: 'tool', tool });
        } else if (block.type === 'selection_request' && block.selection?.options.length) {
            segments.push({ type: 'selection', selection: block.selection });
        }
    }

    return segments;
}

interface SubagentRunMeta {
    name: string;
    status: SubagentLifecycleStatus;
    error?: string | null;
}

export class SubagentStreamState {
    readonly runs = new Map<string, SubagentRunMeta>();
    private readonly nestedBlocks = new Map<string, Map<number, StreamBlock>>();
    private nextBlockIndex = new Map<string, number>();
    private currentTextBlockIndex = new Map<string, number | null>();
    readonly pendingToolResults = new Map<string, string>();

    clear(): void {
        this.runs.clear();
        this.nestedBlocks.clear();
        this.nextBlockIndex.clear();
        this.currentTextBlockIndex.clear();
        this.pendingToolResults.clear();
    }

    upsertRun(
        subagentCallId: string,
        patch: Partial<{
            name: string;
            status: SubagentLifecycleStatus;
            error: string | null;
        }>,
    ): void {
        if (!subagentCallId) return;
        const cur = this.runs.get(subagentCallId);
        this.runs.set(subagentCallId, {
            name: patch.name ?? cur?.name ?? 'subagent',
            status: patch.status ?? cur?.status ?? 'running',
            error: patch.error !== undefined ? patch.error : (cur?.error ?? null),
        });
    }

    startRun(subagentCallId: string, name: string): void {
        this.upsertRun(subagentCallId, { name, status: 'running' });
    }

    endRun(
        subagentCallId: string,
        status: Exclude<SubagentLifecycleStatus, 'running'>,
        error?: string | null,
    ): void {
        const existing = this.runs.get(subagentCallId);
        if (existing) {
            existing.status = status;
            existing.error = error ?? null;
            return;
        }
        this.runs.set(subagentCallId, { name: 'subagent', status, error: error ?? null });
    }

    private getNestedMap(parentCallId: string): Map<number, StreamBlock> {
        if (!this.nestedBlocks.has(parentCallId)) {
            this.nestedBlocks.set(parentCallId, new Map());
            this.nextBlockIndex.set(parentCallId, 0);
            this.currentTextBlockIndex.set(parentCallId, null);
        }
        return this.nestedBlocks.get(parentCallId)!;
    }

    private pendingKey(parentCallId: string, toolCallId: string): string {
        return `${parentCallId}::${toolCallId}`;
    }

    addToolCall(
        parentCallId: string,
        toolCallId: string,
        name: string,
        input: unknown,
        subagent: string | null,
    ): void {
        const blocks = this.getNestedMap(parentCallId);
        let index = this.nextBlockIndex.get(parentCallId) ?? 0;
        this.currentTextBlockIndex.set(parentCallId, null);

        blocks.set(index, {
            index,
            type: 'tool_use',
            content: '',
            toolId: toolCallId,
            toolName: name,
            toolInput: input,
        });

        const pendingOutput = this.pendingToolResults.get(this.pendingKey(parentCallId, toolCallId));
        if (pendingOutput !== undefined) {
            const block = blocks.get(index);
            if (block?.type === 'tool_use') {
                block.toolOutput = pendingOutput;
            }
            this.pendingToolResults.delete(this.pendingKey(parentCallId, toolCallId));
        }

        index += 1;
        this.nextBlockIndex.set(parentCallId, index);

        if (subagent) {
            this.upsertRun(parentCallId, { name: subagent });
        }
    }

    upsertDelegationTaskPanel(toolCallId: string, input: unknown): void {
        const name = readDelegationPanelNameFromToolInput(input);
        if (!toolCallId || !name) return;
        this.upsertRun(toolCallId, { name });
    }

    addToolResult(parentCallId: string, toolCallId: string | null, outputStr: string): void {
        const blocks = this.getNestedMap(parentCallId);
        const toolUseBlocks = Array.from(blocks.values())
            .filter((b): b is StreamBlock & { type: 'tool_use' } => b.type === 'tool_use')
            .sort((a, b) => a.index - b.index);

        let targetBlock = toolCallId
            ? toolUseBlocks.find((b) => normalizeToolCallId(b.toolId) === toolCallId)
            : null;
        if (!targetBlock) {
            targetBlock = toolUseBlocks.find((b) => !b.toolOutput) ?? null;
        }

        if (targetBlock) {
            targetBlock.toolOutput = outputStr;
            return;
        }

        if (toolCallId) {
            this.pendingToolResults.set(this.pendingKey(parentCallId, toolCallId), outputStr);
        }
    }

    addTextDelta(parentCallId: string, text: string): void {
        const blocks = this.getNestedMap(parentCallId);
        let index = this.currentTextBlockIndex.get(parentCallId);
        let textBlock = index != null ? blocks.get(index) : null;

        if (!textBlock || textBlock.type !== 'text') {
            index = this.nextBlockIndex.get(parentCallId) ?? 0;
            textBlock = { index, type: 'text', content: '' };
            blocks.set(index, textBlock);
            this.currentTextBlockIndex.set(parentCallId, index);
            this.nextBlockIndex.set(parentCallId, index + 1);
        }

        textBlock.content += text;
    }

    buildNestedSegments(parentCallId: string): ContentSegment[] {
        const blocks = this.nestedBlocks.get(parentCallId);
        if (!blocks || blocks.size === 0) return [];
        const runMeta = this.runs.get(parentCallId);
        return buildSegmentsFromBlockMap(blocks, {
            subagent: runMeta?.name ?? null,
            subagentCallId: parentCallId,
        });
    }

    attachToRootSegments(rootSegments: ContentSegment[]): ContentSegment[] {
        return rootSegments.map((segment) => {
            if (segment.type !== 'tool' || !segment.tool) return segment;

            const parentId = segment.tool.toolCallId;
            const nestedSegments = this.buildNestedSegments(parentId);
            const runMeta = this.runs.get(parentId);

            if (!runMeta && nestedSegments.length === 0) return segment;

            const subagentRuns: SubagentRun[] = [
                {
                    subagentCallId: parentId,
                    name: runMeta?.name ?? inferSubagentNameFromSegments(nestedSegments),
                    status: runMeta?.status ?? 'completed',
                    error: runMeta?.error ?? null,
                    nestedSegments,
                },
            ];

            return {
                ...segment,
                tool: {
                    ...segment.tool,
                    subagentRuns,
                },
            };
        });
    }
}

export interface FetchedSubagentItem {
    subagentCallId: string | null;
    subagent: string | null;
    segment: ContentSegment;
}

export function attachSubagentRunsFromFetchedItems(
    rootSegments: ContentSegment[],
    nestedItems: FetchedSubagentItem[],
): ContentSegment[] {
    const nestedByParent = new Map<string, ContentSegment[]>();
    const metaByParent = new Map<string, { name: string }>();

    for (const item of nestedItems) {
        if (!item.subagentCallId) continue;
        const list = nestedByParent.get(item.subagentCallId) ?? [];
        list.push(item.segment);
        nestedByParent.set(item.subagentCallId, list);
        if (item.subagent) {
            metaByParent.set(item.subagentCallId, { name: item.subagent });
        }
    }

    if (nestedByParent.size === 0) return rootSegments;

    return rootSegments.map((segment) => {
        if (segment.type !== 'tool' || !segment.tool) return segment;

        const parentId = segment.tool.toolCallId;
        const nestedSegments = nestedByParent.get(parentId);
        if (!nestedSegments?.length) return segment;

        const subagentRuns: SubagentRun[] = [
            {
                subagentCallId: parentId,
                name: metaByParent.get(parentId)?.name ?? inferSubagentNameFromSegments(nestedSegments),
                status: 'restored',
                error: null,
                nestedSegments,
            },
        ];

        return {
            ...segment,
            tool: {
                ...segment.tool,
                subagentRuns,
            },
        };
    });
}

/** In-memory stream accumulator for SSE replay tests and handler wiring. */
export class CommandStreamAccumulator {
    readonly rootBlocks = new Map<number, StreamBlock>();
    readonly subagentState = new SubagentStreamState();
    nextBlockIndex = 0;
    currentTextBlockIndex: number | null = null;
    readonly pendingToolResults = new Map<string, string>();

    reset(): void {
        this.rootBlocks.clear();
        this.subagentState.clear();
        this.nextBlockIndex = 0;
        this.currentTextBlockIndex = null;
        this.pendingToolResults.clear();
    }

    getSegments(): ContentSegment[] {
        const root = buildSegmentsFromBlockMap(this.rootBlocks);
        return this.subagentState.attachToRootSegments(root);
    }

    handleToolCall(event: Record<string, unknown>): void {
        const panelId = resolveSubagentPanelId(event);
        const { subagent } = readSubagentTaggedFields(event);
        const name = String(event.name ?? 'unknown');
        const input = event.input ?? {};
        const toolCallId = normalizeToolCallId(event.tool_call_id ?? event.id ?? `tool-${this.nextBlockIndex}`);

        if (panelId) {
            this.subagentState.addToolCall(panelId, toolCallId, name, input, subagent);
            return;
        }

        if (isCoordinatorDelegationTaskTool(name, input)) {
            this.subagentState.upsertDelegationTaskPanel(toolCallId, input);
        }

        this.currentTextBlockIndex = null;
        this.rootBlocks.set(this.nextBlockIndex, {
            index: this.nextBlockIndex,
            type: 'tool_use',
            content: '',
            toolId: toolCallId,
            toolName: name,
            toolInput: input,
        });

        const pendingOutput = this.pendingToolResults.get(toolCallId);
        if (pendingOutput !== undefined) {
            const block = this.rootBlocks.get(this.nextBlockIndex);
            if (block?.type === 'tool_use') {
                block.toolOutput = pendingOutput;
            }
            this.pendingToolResults.delete(toolCallId);
        }

        this.nextBlockIndex += 1;
    }

    handleToolResult(event: Record<string, unknown>): void {
        const panelId = resolveSubagentPanelId(event);
        const toolCallId =
            event.tool_call_id != null ? normalizeToolCallId(event.tool_call_id) : null;
        const payload = event.content ?? event.output;
        const outputStr =
            payload != null && typeof payload === 'object'
                ? JSON.stringify(payload, null, 2)
                : String(payload ?? '');

        if (panelId) {
            this.subagentState.addToolResult(panelId, toolCallId, outputStr);
            return;
        }

        const toolUseBlocks = Array.from(this.rootBlocks.values())
            .filter((b): b is StreamBlock & { type: 'tool_use' } => b.type === 'tool_use')
            .sort((a, b) => a.index - b.index);

        let targetBlock = toolCallId
            ? toolUseBlocks.find((b) => normalizeToolCallId(b.toolId) === toolCallId)
            : null;
        if (!targetBlock) {
            targetBlock = toolUseBlocks.find((b) => !b.toolOutput) ?? null;
        }

        if (targetBlock) {
            targetBlock.toolOutput = outputStr;
        } else if (toolCallId) {
            this.pendingToolResults.set(toolCallId, outputStr);
        }
    }

    handleTextDelta(event: Record<string, unknown>): void {
        const panelId = resolveSubagentPanelId(event);
        const text = typeof event.text === 'string' ? event.text : '';
        if (!text) return;

        if (panelId) {
            this.subagentState.addTextDelta(panelId, text);
            return;
        }

        let textBlock =
            this.currentTextBlockIndex != null
                ? this.rootBlocks.get(this.currentTextBlockIndex)
                : null;

        if (!textBlock || textBlock.type !== 'text') {
            textBlock = { index: this.nextBlockIndex, type: 'text', content: '' };
            this.rootBlocks.set(this.nextBlockIndex, textBlock);
            this.currentTextBlockIndex = this.nextBlockIndex;
            this.nextBlockIndex += 1;
        }

        textBlock.content += text;
    }

    handleSubagentStart(event: Record<string, unknown>): void {
        const subagentCallId = readSubagentCallIdFromEvent(event);
        const name = typeof event.name === 'string' ? event.name : 'subagent';
        if (!subagentCallId) return;
        this.subagentState.startRun(subagentCallId, name);
    }

    handleSubagentEnd(event: Record<string, unknown>): void {
        const subagentCallId = readSubagentCallIdFromEvent(event);
        if (!subagentCallId) return;
        const rawStatus = typeof event.status === 'string' ? event.status : 'completed';
        const status = (
            ['completed', 'failed', 'interrupted', 'drained'].includes(rawStatus)
                ? rawStatus
                : 'completed'
        ) as Exclude<SubagentLifecycleStatus, 'running'>;
        const error = typeof event.error === 'string' ? event.error : null;
        this.subagentState.endRun(subagentCallId, status, error);
    }

    processEvent(event: Record<string, unknown>): void {
        const type = typeof event.type === 'string' ? event.type : '';
        switch (type) {
            case 'tool_call':
                this.handleToolCall(event);
                break;
            case 'tool_result':
                this.handleToolResult(event);
                break;
            case 'text_delta':
                this.handleTextDelta(event);
                break;
            case 'subagent_start':
                this.handleSubagentStart(event);
                break;
            case 'subagent_end':
                this.handleSubagentEnd(event);
                break;
            default:
                break;
        }
    }
}
